var _react_refresh_temp_1;
_react_refresh_temp_1 = $RefreshSig$();
import { jsx as _jsx } from "react/jsx-runtime";
// '#components' is imported by direct path: its barrel would close the hooks <-> components cycle (see CLAUDE.md).
import { useQueries } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UNKNOWN_ENUM_VALUE, } from '@lucidlink/dto/validation';
import { useModal } from '@lucidlink/uicore';
import { RoleChangedModal } from '#components/modals/RoleChangedModal';
import { MODAL_IDS } from '#constants';
import { useAuthProvider, useLucidSdk } from '#context';
import { canFetchWorkspaceData, currentMemberByWorkspaceIdQueryFactory, useUserWorkspaces, workspaceLoginStatusQueryFactory, } from '#query-hooks';
import { ensureArrayOfIds, getManagedFilespaceIdsDifference, isPendingMember, } from '#services';
export const useUserRoleChangeNotifier = () => {
    _react_refresh_temp_1();
    const { workspaceId: workspaceIdInContext = '' } = useParams();
    const { lucidSdk } = useLucidSdk();
    const { currentUser } = useAuthProvider();
    const { closeModal, openModal } = useModal({
        modalId: MODAL_IDS.RoleChangedModal,
        closeOnNavigate: false,
    });
    const { query: { data: workspaces }, } = useUserWorkspaces();
    // Get workspace login status
    const workspaceLoginStatuses = useQueries({
        queries: workspaces?.map(({ id }) => ({
            ...workspaceLoginStatusQueryFactory({
                lucidSdk,
                workspaceId: id,
            }),
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- pins 'always' to NetworkMode so useQueries options typecheck (lint:tsc needs it; projectService mis-judges as unnecessary)
            networkMode: 'always',
            enabled: Boolean(currentUser?.emailVerified && Boolean(id)),
        })) || [],
    });
    const [roleData, setRoleData] = useState({});
    const workspaceIdAndRoleData = useQueries({
        queries: workspaces
            // Skip pending invites and workspaces we can't yet fetch data for
            ?.filter((workspaceData, index) => !isPendingMember(workspaceData, currentUser) &&
            canFetchWorkspaceData(workspaceLoginStatuses[index]?.data))
            // Fetch data about the current user for each workspace
            .map(({ id: workspaceId }) => {
            return {
                ...currentMemberByWorkspaceIdQueryFactory({
                    lucidSdk,
                    workspaceId,
                }),
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- pins 'always' to NetworkMode so useQueries options typecheck (lint:tsc needs it; projectService mis-judges as unnecessary)
                networkMode: 'always',
                refetchOnWindowFocus: true,
            };
        }) || [],
        // combine the results and stabilize them - combine will rerun if the query results change!
        combine: (results) => results.reduce((acc, { data }) => {
            if (!data) {
                return acc;
            }
            const { filespaceAdminFor, role, workspace: { id: workspaceId }, } = data;
            acc[workspaceId] = { role, filespaceAdminFor };
            return acc;
        }, {}),
    });
    // used below to trigger the useEffect in order to open the
    // RoleChanged modal on filespaceAdminFor change
    const hashKey = Object.entries(roleData)
        .map(([workspaceId, { filespaceAdminFor, role }]) => workspaceId +
        role +
        ensureArrayOfIds(filespaceAdminFor === 'all' ? [] : filespaceAdminFor).join(''))
        .join('');
    useEffect(() => {
        // update role data on query result change
        setRoleData((prev) => {
            return Object.fromEntries(Object.entries(workspaceIdAndRoleData).map(([workspaceId, { filespaceAdminFor, role }]) => {
                const prevRole = prev[workspaceId]?.role ?? UNKNOWN_ENUM_VALUE;
                const prevFilespaceAdminFor = prev[workspaceId]?.filespaceAdminFor ?? {};
                const { demotedFsIds, grantedFsIds, haveManagedFilespacesChanged, } = getManagedFilespaceIdsDifference({
                    managedFilespaceIds: filespaceAdminFor,
                    prevManagedFilespaceIds: prevFilespaceAdminFor,
                });
                return [
                    workspaceId,
                    {
                        prevRole,
                        role,
                        prevFilespaceAdminFor,
                        filespaceAdminFor,
                        haveManagedFilespacesChanged,
                        demotedFsIds,
                        grantedFsIds,
                        roleChanged: Boolean(prevRole) &&
                            prevRole !== UNKNOWN_ENUM_VALUE &&
                            prevRole !== role,
                    },
                ];
            }));
        });
    }, [workspaceIdAndRoleData]);
    useEffect(() => {
        // Role is considered changed if either role or managedFilespaceIds have changed
        // managed filespaces can be changed if the user is a Filespace Admin
        // and has been granted/demoted rights to manage filespaces
        const unhandledRoleChangeWorkspace = Object.entries(roleData).find(([workspaceId, { haveManagedFilespacesChanged, roleChanged }]) => workspaceId === workspaceIdInContext &&
            (roleChanged || haveManagedFilespacesChanged));
        if (unhandledRoleChangeWorkspace) {
            const [id, { demotedFsIds, filespaceAdminFor, grantedFsIds, prevFilespaceAdminFor, prevRole, role, },] = unhandledRoleChangeWorkspace;
            const workspace = workspaces?.find((ws) => ws.id === id);
            if (role !== UNKNOWN_ENUM_VALUE &&
                prevRole !== UNKNOWN_ENUM_VALUE &&
                workspace) {
                openModal({
                    content: (_jsx(RoleChangedModal, { closeModal: closeModal, demotedFsIds: demotedFsIds, grantedFsIds: grantedFsIds, prevRole: prevRole, role: role, workspace: workspace })),
                });
                // reset the trigger flags - roleChanged/haveManagedFilespacesChanged
                setRoleData((prev) => ({
                    ...prev,
                    [id]: {
                        role,
                        prevRole,
                        roleChanged: false,
                        filespaceAdminFor,
                        prevFilespaceAdminFor,
                        demotedFsIds,
                        grantedFsIds,
                        haveManagedFilespacesChanged: false,
                    },
                }));
            }
        }
    }, [
        closeModal,
        openModal,
        roleData,
        workspaceIdInContext,
        workspaces,
        hashKey,
    ]);
};
_react_refresh_temp_1(useUserRoleChangeNotifier, "5seKDwBVU0IwTNFqH1cSyzI9e8c=", false, () => [useParams, useLucidSdk, useAuthProvider, useModal, useUserWorkspaces, useQueries, useQueries]);
